import { useEffect, useState } from 'react'
import { api, type ArtikelBild, type Auswertung } from '../api'
import { anzahl, euro, menge } from '../format'
import { Anteile } from '../komponenten/Anteile'
import { Karte } from '../komponenten/Karte'
import { Rangliste } from '../komponenten/Rangliste'
import { Auswertungsraster } from '../komponenten/Auswertungsraster'

export function Artikel({ von, bis, betriebe }: { von: string; bis: string; betriebe: string[] }) {
  const [nach, setNach] = useState<'umsatz' | 'menge'>('umsatz')
  const [d, setD] = useState<ArtikelBild | null>(null)
  const [kacheln, setKacheln] = useState<Auswertung[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)

  useEffect(() => {
    let weg = false
    setLaedt(true); setFehler(null)
    api.auswertungen('artikel')
      .then(async (liste) => {
        const aktiv = liste.filter((k) => k.aktiv).map((k) => k.schluessel)
        const bild = await api.artikel(von, bis, betriebe, nach, aktiv)
        if (!weg) { setKacheln(liste); setD(bild) }
      })
      .catch((e) => { if (!weg) setFehler(e.message) })
      .finally(() => { if (!weg) setLaedt(false) })
    return () => { weg = true }
  }, [von, bis, betriebe.join(','), nach])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (laedt && !d) return <div className="laedt">Wird geladen …</div>
  if (!d) return null

  const langerZeitraum = d.renner?.some((r) => r.auf_bons === null) ?? false

  const inhalt = {
    'artikel.renner': () => (
      <Karte titel="Renner" neben="netto nach Storno"
             werkzeug={
               <div className="knopfleiste" role="group" aria-label="Sortierung">
                 <button aria-pressed={nach === 'umsatz'} onClick={() => setNach('umsatz')}>
                   nach Umsatz
                 </button>
                 <button aria-pressed={nach === 'menge'} onClick={() => setNach('menge')}>
                   nach Menge
                 </button>
               </div>
             }>
        <Rangliste zeilen={d.renner!} nach={nach} />
        {langerZeitraum && (
          <p className="dezent" style={{ fontSize: 11, marginTop: 12, marginBottom: 0 }}>
            „auf Bons“ wird für Zeiträume über 31 Tage nicht berechnet — die Zahl
            kostet mehr Rechenzeit als sie hier wert ist, und eine geschätzte
            wäre keine.
          </p>
        )}
      </Karte>
    ),
    'artikel.penner': () => (
        <Karte titel="Penner" neben="verkauft, aber am seltensten">
          <Rangliste zeilen={d.penner!} nach="menge" />
        </Karte>
    ),
    'artikel.karteileichen': () => (
        <Karte titel="Karteileichen" neben="im Zeitraum kein einziges Mal verkauft">
          {d.karteileichen!.length ? (
            <table>
              <thead>
                <tr><th>Artikel</th><th className="r">zuletzt gesehen</th></tr>
              </thead>
              <tbody>
                {d.karteileichen!.map((k) => (
                  <tr key={k.article_ref}>
                    <td>{k.artikel}</td>
                    <td className="r dezent">{k.zuletzt ?? 'nie'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="leer">Jeder Artikel der Kasse wurde verkauft.</div>}
        </Karte>
    ),
    'artikel.warengruppen': () => (
      <Karte titel="Warengruppen" >
        {d.warengruppen!.length ? (
          <>
            <Anteile
              anteile={d.warengruppen!.map((w) => ({
                name: w.warengruppe, wert: Number(w.umsatz),
              }))}
              formatiere={euro} />
            <table style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>Warengruppe</th>
                  <th>Art</th>
                  <th className="r">Menge</th>
                  <th className="r">Positionen</th>
                  <th className="r">Umsatz</th>
                </tr>
              </thead>
              <tbody>
                {d.warengruppen!.map((w) => (
                  <tr key={w.warengruppe}>
                    <td>{w.warengruppe}</td>
                    <td className="dezent">
                      {w.art === 'FOOD' ? 'Speise' : w.art === 'BEVERAGE' ? 'Getränk'
                        : w.art === 'OTHER' ? 'Sonstiges' : 'nicht zugeordnet'}
                    </td>
                    <td className="r zahl">{menge(w.menge)}</td>
                    <td className="r zahl dezent">{anzahl(w.positionen)}</td>
                    <td className="r zahl">{euro(w.umsatz)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          {d.warengruppen!.some((w) => w.art === 'UNKNOWN') && (
              <p className="dezent" style={{ fontSize: 11, marginTop: 12, marginBottom: 0 }}>
                Solange Warengruppen nicht Speise, Getränk oder Sonstiges
                zugeordnet sind, gibt es kein Speisen-Getränke-Verhältnis und
                keine Getränkequote. Das System rät die Zuordnung nicht — eine
                geratene wäre in jeder darauf gebauten Zahl unsichtbar.
              </p>
            )}
          </>
        ) : <div className="leer">Keine Warengruppen im Zeitraum.</div>}
      </Karte>
    ),
  }

  return (
    <Auswertungsraster kacheln={kacheln} inhalt={inhalt} />
  )
}
